<template>
    <div class="space-y-4">
        <!-- Address Section -->
        <div>
            <div class="space-y-4">
                <div>
                    <label for="address" class="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <div class="relative">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <font-awesome-icon :icon="faMapMarkerAlt" class="h-5 w-5 text-gray-400" />
                        </div>
                        <GMapAutocomplete
                            id="address_lookup"
                            :ref="autocompleteRef"
                            @place_changed="props.initAutocomplete"
                            :options="{ componentRestrictions: { country: ['US', 'VI', 'AS', 'GU', 'PR', 'MP'] } }"
                            :class="['block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pl-10 pr-3 py-1.5 border', {'border-red-300': form.errors.address || form.errors.address_1 || form.errors.city || form.errors.state || form.errors.zip_code || form.errors.latitude || form.errors.longitude}]"
                        />
                    </div>
                    <input v-model="form.address_1" type="hidden" />
                    <input v-model="form.city" type="hidden" />
                    <input v-model="form.state" type="hidden" />
                    <input v-model="form.zip_code" type="hidden" />
                    <p v-if="addressError" class="mt-1 text-sm text-red-600">{{ addressError }}</p>
                </div>
                <div>
                    <label for="address_2" class="block text-sm font-medium text-gray-700 mb-1">Apt, suite, unit (optional)</label>
                    <input
                        id="address_2"
                        v-model="form.address_2"
                        type="text"
                        maxlength="120"
                        placeholder="Apt 4B"
                        :class="['block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5 border', {'border-red-300': form.errors.address_2}]"
                    />
                    <p v-if="form.errors.address_2" class="mt-1 text-sm text-red-600">{{ form.errors.address_2 }}</p>
                </div>
                <MapPreview 
                    :latitude="form.latitude" 
                    :longitude="form.longitude" 
                />
            </div>
        </div>

        <!-- Details Section -->
        <div>
            <h2 class="text-base font-semibold text-gray-900 mb-3">Details</h2>
            <div class="mb-3">
                <label for="property_structure_type" class="block text-sm font-medium text-gray-700 mb-1">Property Type <span class="text-red-600">*</span></label>
                <select id="property_structure_type" v-model="form.property_structure_type" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5 border" :class="{'border-red-300': form.errors.property_structure_type}">
                    <option value="">Select property type</option>
                    <option v-for="(label, value) in propertyStructureTypes" :key="value" :value="Number(value)">{{ label }}</option>
                </select>
                <p v-if="form.errors.property_structure_type" class="mt-1 text-sm text-red-600">{{ form.errors.property_structure_type }}</p>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <label for="sq_ft" class="block text-sm font-medium text-gray-700 mb-1">Sq. ft</label>
                    <input id="sq_ft" type="number" v-model.number="form.sq_ft" step="1" min="0" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5 border" :class="{'border-red-300': form.errors.sq_ft}" />
                    <p v-if="form.errors.sq_ft" class="mt-1 text-sm text-red-600">{{ form.errors.sq_ft }}</p>
                </div>
                <div>
                    <label for="beds" class="block text-sm font-medium text-gray-700 mb-1">Beds <span class="text-red-600">*</span></label>
                    <input id="beds" type="number" v-model="form.beds" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5 border" :class="{'border-red-300': form.errors.beds}" />
                    <p v-if="form.errors.beds" class="mt-1 text-sm text-red-600">{{ form.errors.beds }}</p>
                </div>
                <div>
                    <label for="baths" class="block text-sm font-medium text-gray-700 mb-1">Baths <span class="text-red-600">*</span></label>
                    <input id="baths" type="number" v-model.number="form.baths" step="0.5" min="0" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5 border" :class="{'border-red-300': form.errors.baths}" />
                    <p v-if="form.errors.baths" class="mt-1 text-sm text-red-600">{{ form.errors.baths }}</p>
                </div>
            </div>
        </div>

        <!-- Financials Section -->
        <div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                    <label for="rent" class="block text-sm font-medium text-gray-700 mb-1">Rent <span class="text-red-600">*</span></label>
                    <div class="relative">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <font-awesome-icon :icon="faDollarSign" class="h-5 w-5 text-gray-400" />
                        </div>
                        <input id="rent" type="number" v-model.number="form.rent" step="0.01" min="0" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pl-10 pr-3 py-1.5 border" :class="{'border-red-300': form.errors.rent}" />
                    </div>
                    <p v-if="form.errors.rent" class="mt-1 text-sm text-red-600">{{ form.errors.rent }}</p>
                </div>
                <div>
                    <label for="deposit" class="block text-sm font-medium text-gray-700 mb-1">Deposit</label>
                    <div class="relative">
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <font-awesome-icon :icon="faDollarSign" class="h-5 w-5 text-gray-400" />
                        </div>
                        <input id="deposit" type="number" v-model.number="form.deposit" step="0.01" min="0" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pl-10 pr-3 py-1.5 border" :class="{'border-red-300': form.errors.deposit}" />
                    </div>
                    <p v-if="form.errors.deposit" class="mt-1 text-sm text-red-600">{{ form.errors.deposit }}</p>
                </div>
                <div>
                    <label for="lease_term" class="block text-sm font-medium text-gray-700 mb-1">Lease Term</label>
                    <select id="lease_term" v-model="form.lease_term" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-1.5 border" :class="{'border-red-300': form.errors.lease_term}">
                        <option value="">Select lease term</option>
                        <option v-for="month in Array.from({length: 24}, (_, i) => i + 1)" :key="month" :value="month">{{ month }} {{ month === 1 ? 'month' : 'months' }}</option>
                    </select>
                    <p v-if="form.errors.lease_term" class="mt-1 text-sm text-red-600">{{ form.errors.lease_term }}</p>
                </div>
            </div>
        </div>

        <!-- Availability + application fee (agents only; fee replaces former utilities / pet columns) -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div :class="showApplicationFeeSection ? '' : 'sm:col-span-3'">
                <h2 class="text-base font-semibold text-gray-900 mb-3">Availability <span class="text-red-600">*</span></h2>
                <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <font-awesome-icon :icon="faCalendar" class="h-5 w-5 text-gray-400" />
                    </div>
                    <input 
                        type="date" 
                        v-model="form.availability_date" 
                        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pl-10 pr-3 py-1.5 border" :class="{'border-red-300': form.errors.availability_date}"
                    />
                </div>
                <p v-if="form.errors.availability_date" class="mt-1 text-sm text-red-600">{{ form.errors.availability_date }}</p>
            </div>
            <div v-if="showApplicationFeeSection" class="sm:col-span-2">
                <h2 class="text-base font-semibold text-gray-900 mb-1">Application fee <span class="text-red-600">*</span></h2>
                <div class="mt-4 space-y-3">
                    <label class="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 p-3 has-[:checked]:border-rent-risk-blue-400 has-[:checked]:bg-blue-50/50">
                        <input
                            v-model="form.is_agent_fee"
                            type="radio"
                            :value="false"
                            class="mt-1 h-4 w-4 border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-500"
                        />
                        <span class="text-sm font-medium text-gray-900">{{ applicantPaysFeeLabel }}</span>
                    </label>
                    <label class="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 p-3 has-[:checked]:border-rent-risk-blue-400 has-[:checked]:bg-blue-50/50">
                        <input
                            v-model="form.is_agent_fee"
                            type="radio"
                            :value="true"
                            class="mt-1 h-4 w-4 border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-500"
                        />
                        <span class="text-sm font-medium text-gray-900">I will cover the application fee on behalf of the applicant.</span>
                    </label>
                </div>
                <p v-if="form.errors.is_agent_fee" class="mt-2 text-sm text-red-600" v-text="form.errors.is_agent_fee" />
                <p v-if="form.errors.add_card" class="mt-2 text-sm text-red-600" v-text="form.errors.add_card" />
                <p v-if="paymentMethodSuccess" class="mt-2 text-sm text-green-600">{{ paymentMethodSuccess }}</p>
                <p v-if="form.is_agent_fee && !hasPaymentMethod && hasStripeSetup" class="mt-2 text-sm text-amber-800">
                    A credit card is required when you cover application fees for applicants. You will be prompted to add a card if you do not have one on file.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faMapMarkerAlt, faDollarSign, faCalendar } from '@fortawesome/pro-solid-svg-icons'
import MapPreview from './MapPreview.vue'

const props = defineProps({
    form: {
        type: Object,
        required: true
    },
    autocompleteRef: {
        type: Object,
        required: true
    },
    initAutocomplete: {
        type: Function,
        required: true
    },
    propertyStructureTypes: {
        type: Object,
        required: true
    },
    screeningFeeAmount: {
        type: Number,
        required: true,
    },
    showApplicationFeeSection: {
        type: Boolean,
        default: false,
    },
    hasPaymentMethod: {
        type: Boolean,
        default: false,
    },
    hasStripeSetup: {
        type: Boolean,
        default: false,
    },
    paymentMethodSuccess: {
        type: String,
        default: null,
    },
})

const applicantPaysFeeLabel = computed(() => {
    const amount = Number(props.screeningFeeAmount)
    const n = Number.isFinite(amount) ? amount : 59
    const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(n)
    return `The applicant will pay the ${formatted} application fee.`
})

// Consolidated address error message
const addressError = computed(() => {
    const addressFields = ['address', 'address_1', 'city', 'state', 'zip_code', 'latitude', 'longitude']
    for (const field of addressFields) {
        if (props.form.errors[field]) {
            const error = props.form.errors[field]
            return Array.isArray(error) ? error[0] : error
        }
    }
    return null
})
</script>
